import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import Moment from "moment";
import { extendMoment } from "moment-range";
import CalenderWrapper from "./Calender.style";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Typography } from "antd";
import CalenderEventDetails from "./CalenderEventDetails";
import { useIntl } from "react-intl";
import {
  convertAvailabilityEventsToCalender,
  convertAvailabilityEventsFromCalender,
} from "../util/helpers";
import PropTypes from "prop-types";
const { Title } = Typography;
const DragAndDropCalendar = withDragAndDrop(Calendar);
const moment = extendMoment(Moment);
const localizer = momentLocalizer(moment);

/**
 * PeriodsCalenderPicker component - An interactive calendar interface for selecting and
 * managing time periods with drag and drop functionality.
 *
 * This component allows users to create, edit, resize, and delete periods on a calendar,
 * with support for custom event details and validation.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.record - Array of existing period records to display on calendar
 * @param {React.ReactNode} props.comp - Component to render for event details entry
 * @param {React.ReactNode} props.title - Title displayed at the top of the calendar
 * @param {Function} props.onChange - Callback when periods are changed, added or removed
 * @param {string|number} [props.entityId] - ID of the entity these periods belong to
 * @param {boolean} [props.preserveScrollPosition=false] - Whether to maintain scroll position during updates
 * @returns {React.ReactElement} Calendar picker for managing periods
 */
const PeriodsCalenderPicker = (props) => {
  const {
    record,
    comp,
    title,
    onChange,
    entityId,
    preserveScrollPosition = false,
  } = props;
  const [activeEvent, setActiveEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const displayDragItemInCell = true;
  const localeLang = "he";
  const [draggedEvent, setDraggedEvent] = useState(null);
  const intl = useIntl();

  // Store scrollPosition to prevent jumps
  const [scrollPosition, setScrollPosition] = useState(null);
  const calendarRef = React.useRef();

  // Store date to prevent calendar from jumping
  const [currentDate, setCurrentDate] = useState(
    moment().startOf("week").toDate()
  );

  /**
   * Captures the current scroll position of the calendar view
   * Used to maintain scroll position during state updates
   */
  const captureScrollPosition = useCallback(() => {
    if (preserveScrollPosition && calendarRef.current) {
      const calendarDom = calendarRef.current.querySelector(".rbc-time-view");
      if (calendarDom) {
        setScrollPosition({
          scrollTop: calendarDom.scrollTop,
          scrollLeft: calendarDom.scrollLeft,
        });
      }
    }
  }, [preserveScrollPosition]);

  /**
   * Restores previously captured scroll position after DOM updates
   * Prevents the calendar from jumping when events are modified
   */
  const restoreScrollPosition = useCallback(() => {
    if (preserveScrollPosition && calendarRef.current && scrollPosition) {
      const calendarDom = calendarRef.current.querySelector(".rbc-time-view");
      if (calendarDom) {
        calendarDom.scrollTop = scrollPosition.scrollTop;
        calendarDom.scrollLeft = scrollPosition.scrollLeft;
      }
    }
  }, [preserveScrollPosition, scrollPosition]);

  /**
   * Handles date change in calendar view to prevent navigation jumps
   *
   * @param {Date} date - The new date to navigate to
   */
  const handleCalendarNavigate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  /**
   * Updates the events displayed on the calendar when record prop changes
   * Manages scroll position during the update to prevent visual jumps
   */
  useEffect(() => {
    // Capture scroll position before update
    captureScrollPosition();

    // Ensure record is an array before conversion
    if (Array.isArray(record)) {
      const converted = convertAvailabilityEventsToCalender(record);
      setEvents(converted);
    } else {
      setEvents([]);
    }

    // Restore scroll position after update
    setTimeout(restoreScrollPosition, 0);
  }, [record, captureScrollPosition, restoreScrollPosition]);

  /**
   * Checks if a new/modified event overlaps with existing events
   * Used to prevent overlapping periods on the calendar
   *
   * @param {Object} newEvent - The event to check for overlaps
   * @returns {boolean} True if the event overlaps with existing events
   */
  const isOverlapping = useCallback(
    (newEvent) => {
      let overlapping = false;
      // Filter out the current event when checking for overlaps
      const filtered = events.filter((i) => i.id !== newEvent.id);
      filtered.forEach((item) => {
        const range1 = moment.range(newEvent.start, newEvent.end);
        const range2 = moment.range(item.start, item.end);
        if (range1.overlaps(range2, { adjacent: false })) {
          overlapping = true;
        }
      });
      return overlapping;
    },
    [events]
  );

  /**
   * Updates an existing event with new start/end times
   * Validates against overlaps before applying changes
   *
   * @param {Object} changed - The event being changed
   * @param {Date} start - New start time
   * @param {Date} end - New end time
   */
  const changeEvent = useCallback(
    (changed, start, end) => {
      captureScrollPosition();

      const changedEvent = {
        ...changed,
        start,
        end,
      };

      if (!isOverlapping(changedEvent)) {
        const nextEvents = events.map((existingEvent) => {
          return existingEvent.id === changedEvent.id
            ? changedEvent
            : existingEvent;
        });

        setActiveEvent(changedEvent);
        setEvents(nextEvents);

        // Convert the updated events to the format expected by parent component
        const convertedEvents = convertAvailabilityEventsFromCalender(
          nextEvents,
          entityId
        );
        onChange(convertedEvents);

        setTimeout(restoreScrollPosition, 0);
      }
    },
    [
      events,
      isOverlapping,
      onChange,
      entityId,
      captureScrollPosition,
      restoreScrollPosition,
    ]
  );

  /**
   * Handles dragging and moving an event to a new position
   *
   * @param {Object} param0 - Object containing event data and new positions
   * @param {Object} param0.event - The event being moved
   * @param {Date} param0.start - New start time
   * @param {Date} param0.end - New end time
   */
  const moveEvent = useCallback(
    ({ event, start, end }) => {
      changeEvent(event, start, end);
    },
    [changeEvent]
  );

  /**
   * Handles resizing an event to change its duration
   *
   * @param {Object} param0 - Object containing event data and new times
   * @param {Object} param0.event - The event being resized
   * @param {Date} param0.start - New start time
   * @param {Date} param0.end - New end time
   */
  const resizeEvent = useCallback(
    ({ event, start, end }) => {
      changeEvent(event, start, end);
    },
    [changeEvent]
  );

  /**
   * Sets the currently selected event for editing
   *
   * @param {Object} newEvent - The event to edit
   */
  const editEvent = useCallback((newEvent) => {
    setActiveEvent(newEvent);
  }, []);

  /**
   * Removes the currently active event from the calendar
   * Updates the parent component with the changes
   */
  const removeEvent = useCallback(() => {
    if (!activeEvent) return;

    const nextEvents = events.filter((item) => item.id !== activeEvent.id);
    setEvents(nextEvents);
    setActiveEvent(null);

    // Convert the updated events to the format expected by parent component
    const convertedEvents = convertAvailabilityEventsFromCalender(
      nextEvents,
      entityId
    );
    onChange(convertedEvents);
  }, [activeEvent, events, onChange, entityId]);

  /**
   * Stores the event when drag starts for use in drag-and-drop operations
   *
   * @param {Object} event - The event being dragged
   */
  const handleDragStart = useCallback((event) => {
    setDraggedEvent(event);
  }, []);

  /**
   * Returns the dragged event for the drag-and-drop operation
   *
   * @returns {Object|null} The event being dragged
   */
  const dragFromOutsideItem = useCallback(() => {
    return draggedEvent;
  }, [draggedEvent]);

  /**
   * Handles dropping an event from outside the calendar or cell
   *
   * @param {Object} param0 - Object with drop position details
   * @param {Date} param0.start - Start time at drop position
   * @param {Date} param0.end - End time at drop position
   * @param {boolean} param0.allDay - Whether it's an all-day slot
   */
  const onDropFromOutside = useCallback(
    ({ start, end, allDay }) => {
      if (!draggedEvent) return;

      const event = {
        id: draggedEvent.id,
        title: draggedEvent.title,
        start,
        end,
      };
      setDraggedEvent(null);
      moveEvent({ event, start, end });
    },
    [draggedEvent, moveEvent]
  );

  /**
   * Creates a new event when a calendar slot is selected
   * Validates against overlaps before adding the event
   *
   * @param {Object} newEvent - The new event to create
   */
  const createEvent = useCallback(
    (newEvent) => {
      captureScrollPosition();

      if (!isOverlapping(newEvent)) {
        // Store place and description in the event object so they're preserved
        newEvent["id"] = events.length + 1;
        newEvent["place"] = ""; // Default place
        newEvent["description"] = ""; // Default description
        setActiveEvent(newEvent);
        const nextEvents = [...events, { ...newEvent }];
        setEvents(nextEvents);

        // Convert the updated events to the format expected by parent component
        const convertedEvents = convertAvailabilityEventsFromCalender(
          nextEvents,
          entityId
        );
        onChange(convertedEvents);

        setTimeout(restoreScrollPosition, 0);
      }
    },
    [
      events,
      isOverlapping,
      onChange,
      entityId,
      captureScrollPosition,
      restoreScrollPosition,
    ]
  );

  /**
   * Handles double-click events to open the event editor
   *
   * @param {Object} event - The event that was double-clicked
   */
  const onDoubleClickEvent = useCallback(
    (event) => {
      editEvent(event);
    },
    [editEvent]
  );

  // Memoize formats to prevent unnecessary re-renders
  const formats = useMemo(
    () => ({
      dayFormat: "dddd",
    }),
    []
  );

  // Calculate scroll time based on current date
  const scrollTime = useMemo(
    () => moment(currentDate).clone().add(6, "hours").toDate(),
    [currentDate]
  );

  return (
    <CalenderWrapper>
      <Title
        style={{
          fontSize: "30px",
          fontWeight: 500,
          lineHeight: 1.27,
          color: "rgba(0, 0, 0, 0.85)",
          margin: "13px 0 40px 0 ",
          textAlign: "center",
        }}
      >
        {title}
      </Title>
      <div className="periodsCalenderContainer" ref={calendarRef}>
        {/* Event details editor panel */}
        <CalenderEventDetails
          onChange={changeEvent}
          activeEvent={activeEvent}
          onRemove={removeEvent}
          comp={comp}
        />

        {/* Calendar with drag-and-drop functionality */}
        <DragAndDropCalendar
          popup
          formats={formats}
          rtl={localeLang === "he"}
          toolbar={false}
          selectable
          step={15}
          localizer={localizer}
          events={events}
          defaultView={Views.WEEK}
          date={currentDate}
          defaultDate={currentDate}
          scrollToTime={scrollTime}
          onEventDrop={moveEvent}
          resizable
          onEventResize={resizeEvent}
          onSelectSlot={createEvent}
          onSelectEvent={editEvent}
          onDoubleClickEvent={onDoubleClickEvent}
          onNavigate={handleCalendarNavigate}
          dragFromOutsideItem={
            displayDragItemInCell ? dragFromOutsideItem : null
          }
          onDropFromOutside={onDropFromOutside}
          handleDragStart={handleDragStart}
          style={{ height: "100%", width: "100%" }}
        />
      </div>
    </CalenderWrapper>
  );
};

// Define prop types for component documentation and validation
PeriodsCalenderPicker.propTypes = {
  title: PropTypes.node,
  comp: PropTypes.node,
  record: PropTypes.array,
  onChange: PropTypes.func,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  preserveScrollPosition: PropTypes.bool,
};

/**
 * Memoized PeriodsCalenderPicker to prevent unnecessary re-renders
 * Uses custom comparison function to determine when to re-render
 *
 * @component
 */
export default React.memo(PeriodsCalenderPicker, (prevProps, nextProps) => {
  // Only re-render if these essential props change
  return (
    prevProps.entityId === nextProps.entityId &&
    prevProps.onChange === nextProps.onChange &&
    // Use JSON.stringify for deep comparison of record arrays
    JSON.stringify(prevProps.record) === JSON.stringify(nextProps.record)
  );
});
